'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import DocumentUploadSection from './DocumentUploadSection';
import PaymentSection from './PaymentSection';
import VerificationStatus from './VerificationStatus';

interface VerificationFormProps {
  memberName: string;
}

type VerificationStep = 'documents' | 'payment' | 'submitted';

export default function VerificationForm({ memberName }: VerificationFormProps) {
  const [currentStep, setCurrentStep] = useState<VerificationStep>('documents');
  const [uploadedDocs, setUploadedDocs] = useState<Record<string, boolean>>({});

  const allDocsUploaded = [
    'application-form',
    'aadhaar-front',
    'aadhaar-back',
    'pan-card',
    'selfie',
    'social-proof',
  ].every((doc) => uploadedDocs[doc]);

  const handleDocUpload = (docId: string, uploaded: boolean) => {
    setUploadedDocs((prev) => ({ ...prev, [docId]: uploaded }));
  };

  const handlePaymentSuccess = () => {
    setCurrentStep('submitted');
  };

  const steps = [
    { id: 'documents', label: 'Upload Documents', icon: 'DocumentArrowUpIcon' },
    { id: 'payment', label: 'Pay ₹500 Fee', icon: 'CreditCardIcon' },
    { id: 'submitted', label: 'Submitted', icon: 'CheckCircleIcon' },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 xl:gap-12">
      {/* Main Content */}
      <div className="lg:col-span-2">
        {/* Welcome Banner */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
            <Icon name="UserCircleIcon" size={20} variant="solid" className="text-white" />
          </div>
          <div>
            <p className="text-secondary font-700 text-sm">Welcome, {memberName}!</p>
            <p className="text-green-700 text-xs">
              You are logged in. Complete document verification to activate your FCEP membership.
            </p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="bg-card border border-border rounded-2xl p-5 mb-6">
          <div className="flex items-center gap-2">
            {steps.map((step, index) => (
              <React.Fragment key={`vstep-${step.id}`}>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                      currentStep === step.id
                        ? 'bg-primary text-white'
                        : steps.findIndex((s) => s.id === currentStep) > index
                        ? 'bg-secondary text-white' :'bg-muted text-muted-foreground'
                    }`}
                  >
                    {steps.findIndex((s) => s.id === currentStep) > index ? (
                      <Icon name="CheckIcon" size={16} variant="outline" />
                    ) : (
                      <Icon
                        name={step.icon as Parameters<typeof Icon>[0]['name']}
                        size={16}
                        variant="outline"
                      />
                    )}
                  </div>
                  <span
                    className={`text-sm font-500 truncate hidden sm:block ${
                      currentStep === step.id ? 'text-primary font-600' : 'text-muted-foreground'
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-shrink-0 h-0.5 w-8 lg:w-16 transition-colors ${
                      steps.findIndex((s) => s.id === currentStep) > index
                        ? 'bg-secondary' :'bg-border'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step Content */}
        {currentStep === 'documents' && (
          <DocumentUploadSection
            uploadedDocs={uploadedDocs}
            onDocUpload={handleDocUpload}
            onProceed={() => setCurrentStep('payment')}
            allDocsUploaded={allDocsUploaded}
          />
        )}

        {currentStep === 'payment' && (
          <PaymentSection
            onBack={() => setCurrentStep('documents')}
            onPaymentSuccess={handlePaymentSuccess}
          />
        )}

        {currentStep === 'submitted' && (
          <VerificationStatus memberName={memberName} />
        )}
      </div>

      {/* Sidebar */}
      <div className="lg:col-span-1 space-y-4">
        {/* What to Upload */}
        <div className="bg-card border border-border rounded-2xl p-5">
          <h3 className="text-base font-700 text-foreground mb-4 flex items-center gap-2">
            <Icon name="DocumentTextIcon" size={18} variant="outline" className="text-primary" />
            Required Documents
          </h3>
          <ul className="space-y-3">
            {[
              { id: 'req-app', icon: 'DocumentArrowDownIcon', label: 'FCEP Application Form', note: 'Download, fill & upload' },
              { id: 'req-aadh', icon: 'IdentificationIcon', label: 'Aadhaar Card', note: 'Front and back sides' },
              { id: 'req-pan', icon: 'CreditCardIcon', label: 'PAN Card', note: 'Clear photo/scan' },
              { id: 'req-selfie', icon: 'UserCircleIcon', label: 'Recent Selfie / Photo', note: 'Clear face visible' },
              { id: 'req-social', icon: 'DevicePhoneMobileIcon', label: 'Social Media Proof', note: 'Profile screenshot' },
            ].map((item) => (
              <li key={item.id} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <Icon
                    name={item.icon as Parameters<typeof Icon>[0]['name']}
                    size={16}
                    variant="outline"
                    className="text-primary"
                  />
                </div>
                <div>
                  <p className="text-sm font-600 text-foreground">{item.label}</p>
                  <p className="text-xs text-muted-foreground">{item.note}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Fee Info */}
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="BanknotesIcon" size={18} variant="outline" className="text-accent" />
            <h3 className="text-base font-700 text-amber-800">₹500 Verification Fee</h3>
          </div>
          <p className="text-amber-700 text-sm leading-relaxed mb-3">
            This is a one-time fee for FCEP document verification and membership activation.
          </p>
          <ul className="space-y-1.5">
            {[
              'Identity verification processing',
              'FCEP Verified Member badge',
              'Access to all skill programs',
              'Lifetime FCEP membership',
            ].map((item) => (
              <li key={`fee-item-${item.substring(0, 15).replace(/\s/g, '-')}`} className="flex items-center gap-2 text-xs text-amber-700">
                <Icon name="CheckCircleIcon" size={14} variant="solid" className="text-accent flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
          <div className="mt-4 pt-4 border-t border-amber-200">
            <p className="text-xs text-amber-600">
              Payment via UPI or Card. Secure payment processing.
            </p>
          </div>
        </div>

        {/* Help */}
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-sm font-600 text-foreground mb-2">Need Help?</p>
          <div className="space-y-2">
            <a
              href="tel:9007493290"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <Icon name="PhoneIcon" size={14} variant="outline" />
              +91 9007493290
            </a>
            <a
              href="mailto:www.facebook.earning@gmail.com"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors break-all"
            >
              <Icon name="EnvelopeIcon" size={14} variant="outline" />
              www.facebook.earning@gmail.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}